import React, { Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import { SearchInput } from '../search-input/search-input';
import DefaultButton from '../default-button/default-button';
import { MINIMAL_SEARCH_LENGTH, SEARCH_TERM_MIN_LENGTH } from '../../../constants/common.constants';
import './multi-selection-dropdown.scss';
import { FormattedMessage } from 'react-intl';
import { MultiSelectOption } from '../multi-select-list/_multi-select-option';
import Text from '../text/text';
import { MeetingProgress } from '../../../api/control.dao';
import { ProgressDotIndicator } from '../progress-dot-indicator/progress-dot-indicator';
import { MeetingProgressLabel, MeetingProgressList } from '../../../constants/meeting.constants';
import { Popover } from '../popover/popover';
import { LOADING } from '../../../constants/common-labels.constants';

export interface IItemProps {
    id: string;
    label: string;
}

export interface IProps {
    entityName: string | ReactElement;
    onApply: (params: string) => void;
    classNames?: string | null;
    disabled?: boolean;
    onFetchItems: ((query?: string) => Promise<IItemProps[]>) | ((query?: string) => IItemProps[]);
    noItemsPlaceholder?: ReactElement;
    withSearch?: boolean;
    withProgress?: boolean;
    withIcon?: ReactElement;
    savedItems?: string[];
    customReset?: () => void;
    withoutHighlight?: boolean;
    isResetButtonDisabled?: boolean;
    setResetButtonStatus?: Dispatch<SetStateAction<boolean>>;
    withBESearch?: boolean;
    searchPlaceholder?: string;
    noSearchResultText?: string | ReactElement;
    withAddButton?: boolean;
    isSetInitialItems?: boolean;
    customTop?: number | string;
    customLeft?: number;
}

const MultiSelectionDropdown = (props: IProps) => {
    const {
        entityName,
        classNames,
        onApply,
        disabled,
        onFetchItems,
        noItemsPlaceholder,
        withIcon,
        withSearch = true,
        withProgress = false,
        savedItems = [],
        customReset,
        withoutHighlight,
        isResetButtonDisabled = true,
        setResetButtonStatus,
        withBESearch = false,
        searchPlaceholder,
        noSearchResultText,
        withAddButton,
        customTop,
        isSetInitialItems = false,
        customLeft = 0,
    } = props;


    const [isOpen, toggleOptions] = useState(false);
    const [isFetching, setFetching] = useState(false);
    const [items, setItems] = useState<IItemProps[]>([]);
    const [appliedItems, setAppliedItems] = useState<string[]>(savedItems);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const roleButtonRef = useRef(null);
    const isActiveInForm = withAddButton && isOpen;
    const fetchItems = async (query?: string) => {
        setFetching(true);
        const response = await onFetchItems(query);
        if (response) {
            setItems(response);
            setFetching(false);
        }
    };

    const setStatusItems = () => {
        setItems(MeetingProgressList.map(status => {
            return {
                id: status,
                label: MeetingProgressLabel[status]
            };
        }));
    };

    const onDropdownClick = () => {
        setSearchTerm('');
        toggleOptions(prev => !prev);

        if (!isOpen) {
            onFetchItems ? fetchItems() : setStatusItems();
            setSelectedItems(appliedItems);
        }
    };

    const getStatus = (label: string): MeetingProgress => {
        return MeetingProgressList.find(status => status === label ?? label);
    };

    const onOptionClick = (itemId: string) => {
        const isSelected = selectedItems.find(item => item === itemId);
        setResetButtonStatus?.(true);
        if (isSelected) {
            setSelectedItems(selectedItems.filter(item => item !== itemId));
        } else {
            setSelectedItems([...selectedItems, itemId]);
        }
    };

    const onReset = () => {
        if (customReset) {
            customReset();
            toggleOptions(prev => !prev);
        } else {
            setSelectedItems([]);
        }
    };

    const onApplyAction = () => {
        toggleOptions(false);
        setAppliedItems(selectedItems);
        onApply(selectedItems.join());
        setItems([]);

    };

    const trimmedSearchTerm = searchTerm.trim();
    const filteredItems = (trimmedSearchTerm.length < SEARCH_TERM_MIN_LENGTH) ? items :
        items.filter(item => {
            return item.label.toLowerCase().includes(trimmedSearchTerm.toLowerCase());
        });

    const entityNameUpper = `${(entityName as string).charAt(0).toUpperCase()}${(entityName as string).slice(1)}`;
    const dropDownLabel = appliedItems.length ? `${entityNameUpper}: ${appliedItems.length}` : entityNameUpper;

    useEffect(() => {
        if (!isSetInitialItems)
            setAppliedItems([]);
    }, []);

    useEffect(() => {
        if (withBESearch && (!searchTerm || searchTerm.trim().length >= MINIMAL_SEARCH_LENGTH)) {
            fetchItems(searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        if (isSetInitialItems) {
            setSelectedItems(savedItems);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isSetInitialItems && savedItems.length) setAppliedItems(savedItems);
    }, [savedItems.length]);

    return (
        <div className={classnames('multi-selection-dropdown', classNames,
            { 'multi-selection-dropdown--disabled': disabled })}
        >
            {withAddButton ?
                <DefaultButton
                    text={entityName}
                    onClick={onDropdownClick}
                    className={classnames('default-button__add-button',
                        {
                            'default-button__add-button-active': isOpen,
                        }
                    )}
                    ref={roleButtonRef}
                /> :
                <div
                    ref={roleButtonRef}
                    onClick={onDropdownClick}
                    className={classnames('cell-header-btn', { 'cell-header-btn--active': isOpen || (!withoutHighlight && appliedItems.length) })}
                >
                    {withIcon ?? <Text message={dropDownLabel} withEllipsis />}
                </div>
            }

            <Popover
                padding={0}
                isOpen={isOpen}
                buttonRef={roleButtonRef}
                onClickOutside={() => toggleOptions(false)}
                customWidth={220}
                customLeft={customLeft}
                customTop={customTop || 0}
            >
            <div
                className={classnames('multi-selection-dropdown__options',
                    {
                        'active-in-form': isActiveInForm,
                    }
                )}
            >
                <>
                    {items.length || searchTerm ?
                        <>
                            {withSearch && <div className='multi-selection-dropdown__search'>
                                <SearchInput
                                    placeholder={searchPlaceholder || `Search ${entityNameUpper}`}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    value={searchTerm}
                                />
                            </div>
                            }

                            <div className='multi-selection-dropdown__list'>
                                {filteredItems.length ?
                                    filteredItems.map(item => {
                                        const isSelected = !!selectedItems.find(i => i === item.id);
                                        return (
                                            <div
                                                key={item.id}
                                                onClick={() => onOptionClick(item.id)}
                                                className={classnames('multi-selection-dropdown__option', {
                                                    'multi-selection-dropdown__option--selected': isSelected
                                                })}
                                            >
                                                {withProgress ? (
                                                    <div className='multi-selection-dropdown__meeting-progress'>
                                                        <ProgressDotIndicator currentProgress={getStatus(item.id)} />
                                                        <Text
                                                            tag='div'
                                                            className='multi-selection-dropdown__option-label multi-selection-dropdown__status-label'
                                                            message={item.label}
                                                            withEllipsis
                                                        />
                                                    </div>
                                                ) : (
                                                    <Text
                                                        tag='div'
                                                        className='multi-selection-dropdown__option-label'
                                                        message={item.label}
                                                        withEllipsis
                                                    />
                                                )}
                                                <MultiSelectOption
                                                    isSelected={isSelected}
                                                />
                                            </div>
                                        );
                                    }) : <div
                                        className='multi-selection-dropdown__placeholder'>{noSearchResultText || `No ${entityName} found.`}</div>
                                }
                            </div>
                            <div className='multi-selection-dropdown__button-bar'>
                                <DefaultButton
                                    modifier='cancel'
                                    text='Reset'
                                    onClick={onReset}
                                    className='read-only-textarea__controls-button'
                                    disabled={customReset ? !isResetButtonDisabled : !selectedItems.length}
                                />
                                <DefaultButton
                                    modifier='confirm-safe'
                                    text='Apply'
                                    onClick={onApplyAction}
                                    className='read-only-textarea__controls-button'
                                />
                            </div>
                        </> : isFetching ? (
                            <div className='multi-selection-dropdown__placeholder'>
                                <FormattedMessage
                                    {...LOADING}
                                    values={{
                                        suffix: '...'
                                    }}
                                />
                            </div>
                        ) : noItemsPlaceholder
                    }
                </>
            </div>
            </Popover>
        </div>
    );
};

export default MultiSelectionDropdown;
